<template>
  <div class="manual-issue-coupon">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>手动发券</h2>
        </div>
      </template>
      
      <!-- 发券表单 -->
      <el-form :model="issueForm" class="issue-form">
        <el-row :gutter="24">
          <el-col :span="12">
            <!-- 选择优惠券 -->
            <el-form-item label="选择优惠券" prop="coupon_id" required>
              <el-select v-model="issueForm.coupon_id" placeholder="请选择优惠券" style="width: 100%;" filterable>
                <el-option
                  v-for="coupon in availableCoupons"
                  :key="coupon.id"
                  :label="coupon.title"
                  :value="coupon.id"
                >
                  <div class="coupon-option">
                    <div class="coupon-title">{{ coupon.title }}</div>
                    <div class="coupon-meta">
                      <span class="coupon-category">{{ this.getCategoryName(coupon.category) }}</span>
                      <span class="coupon-quantity">库存: {{ coupon.available_quantity }}</span>
                    </div>
                  </div>
                </el-option>
              </el-select>
              <div class="form-hint">请选择要发放的优惠券</div>
            </el-form-item>
          </el-col>
          
          <el-col :span="12">
            <!-- 搜索用户 -->
            <el-form-item label="选择用户" prop="user_id" required>
              <el-select v-model="issueForm.user_id" placeholder="填写用户手机号" style="width: 100%;" filterable remote :remote-method="searchUsers" :loading="searchLoading">
                <el-option
                  v-for="user in searchUsersList"
                  :key="user.id"
                  :label="user.name || user.nickname || user.phone"
                  :value="user.id"
                >
                  <div class="user-option">
                    <div class="user-name">{{ user.name || user.nickname || '未知用户' }}</div>
                    <div class="user-phone">{{ user.phone || '无手机号' }}</div>
                  </div>
                </el-option>
              </el-select>
              <div class="form-hint">请输入手机号</div>
            </el-form-item>
          </el-col>
        </el-row>
        
        <!-- 发券操作 -->
        <el-row>
          <el-col :span="24" class="form-actions">
            <el-button type="primary" @click="submitIssue" :loading="submitting" size="large" :icon="CircleCheckFilled">
              发放优惠券
            </el-button>
            <el-button @click="resetForm" size="large" :icon="RefreshRight">
              重置
            </el-button>
          </el-col>
        </el-row>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { CircleCheckFilled, RefreshRight } from '@element-plus/icons-vue';
import couponApi from '../../api/coupon.js';

export default {
  name: 'ManualIssueCoupon',
  setup() {
    // 可用优惠券列表
    const availableCoupons = ref([]);
    // 搜索用户列表
    const searchUsersList = ref([]);
    // 加载状态
    const loading = ref(false);
    const submitting = ref(false);
    const searchLoading = ref(false);
    
    // 发券表单数据
    const issueForm = reactive({
      coupon_id: '',
      user_id: '',
    });
    
    // 券分类名称映射
    const categoryNames = {
      authentication: '鉴别服务券',
      maintenance: '保养券',
      pre_order: '新品预定权益',
      activity_invitation: '活动邀约权益',
      home_recycle: '上门回收券',
      repair: '维修券',
      peripheral_exchange: '周边兑换券',
      gift_exchange: '赠品兑换券',
    };

    // 获取分类中文名称
    const getCategoryName = (category) => {
      return category ? (categoryNames[category] || category) : '';
    };

    // 加载可用优惠券列表
    const loadAvailableCoupons = () => {
      loading.value = true;
      
      couponApi.getManualIssueData({ status: 'active' })
        .then(response => {
          availableCoupons.value = response.data || [];
        })
        .catch(error => {
          ElMessage.error('获取优惠券列表失败：' + (error.message || '网络错误'));
          console.error('获取优惠券列表失败：', error);
        })
        .finally(() => {
          loading.value = false;
        });
    };

    // 搜索用户
    const searchUsers = (keyword) => {
      if (!keyword) {
        searchUsersList.value = [];
        return;
      }
      
      searchLoading.value = true;
      
      couponApi.searchUsers({ keyword })
        .then(response => {
          searchUsersList.value = response.data || [];
        })
        .catch(error => {
          ElMessage.error('搜索用户失败：' + (error.message || '网络错误'));
          console.error('搜索用户失败：', error);
        })
        .finally(() => {
          searchLoading.value = false;
        });
    };

    // 重置表单
    const resetForm = () => {
      issueForm.coupon_id = '';
      issueForm.user_id = '';
    };

    // 提交发券
    const submitIssue = () => {
      // 验证表单
      if (!issueForm.coupon_id) {
        ElMessage.error('请选择优惠券');
        return;
      }
      if (!issueForm.user_id) {
        ElMessage.error('请选择用户');
        return;
      }
      
      submitting.value = true;
      
      // 调用API发放优惠券
      couponApi.manualIssueCoupon({
        coupon_id: issueForm.coupon_id,
        user_id: issueForm.user_id,
      })
        .then(response => {
          ElMessage.success('发券成功');
          // 重置表单
          resetForm();
          // 重新加载优惠券列表（更新库存）
          loadAvailableCoupons();
        })
        .catch(error => {
          ElMessage.error('发券失败：' + (error.message || '网络错误'));
          console.error('发券失败：', error);
        })
        .finally(() => {
          submitting.value = false;
        });
    };

    // 初始化数据
    onMounted(() => {
      loadAvailableCoupons();
    });

    return {
      issueForm,
      availableCoupons,
      searchUsersList,
      loading,
      submitting,
      searchLoading,
      getCategoryName,
      searchUsers,
      resetForm,
      submitIssue,
    };
  },
};
</script>

<style scoped>
.manual-issue-coupon {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.issue-form {
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
  justify-content: flex-start;
}

.form-actions .el-button {
  padding: 12px 24px;
  font-size: 14px;
}

.form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  line-height: 1.4;
}

.coupon-option {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coupon-title {
  font-weight: bold;
  color: #333;
}

.coupon-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #909399;
}

.user-option {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-name {
  font-weight: bold;
  color: #333;
}

.user-phone {
  font-size: 12px;
  color: #909399;
}

.el-form-item {
  margin-bottom: 20px;
}
</style>